import styled from '@emotion/styled'
import { hasPermission, useRolePermissionsContext } from 'contexts/permissions'
import { useSessionContext } from 'contexts/session'
import useMaterialNotification from 'hooks/useMaterialNotification'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Header from '../components/Header'
import Spacings from '../figma/tokens/Spacings'
import { SentryInit } from '../helpers/sentryHelpers'
import FigmaBox from '../mynt-components/components/FigmaBox'
import { BO_LAYOUT_SIZE } from '../mynt-components/WeakHardCodedSizes'
import { loginRoutes } from './loginRoutes'
import { isCustomRouter, protectedRoutes } from './protectedRoutes'

const RedirectToStart = () => {
  const notify = useMaterialNotification()

  const path = useLocation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    notify('You do not have permission to access this page', 'error')
  }, [])

  return <Navigate to={`/start?redirect-uri=${path.pathname}`} />
}

const Router = () => {
  const rolePermissions = useRolePermissionsContext()
  const { role, isLoading } = useSessionContext()

  return (
    <BrowserRouter>
      <SentryInit />
      <Routes>
        {loginRoutes.map(({ component: Component, path }) => (
          <Route
            path={path}
            key={path}
            element={
              <>
                <Header />
                <Component />
              </>
            }
          />
        ))}
        {protectedRoutes.map((route, index) => {
          if (!hasPermission(rolePermissions, role, route.permissions)) {
            if (isLoading) return null

            // biome-ignore lint/suspicious/noArrayIndexKey: we have no other unique key since route is potentially a custom route
            return <Route path="*" key={`no-permission-${index}`} element={<RedirectToStart />} />
          }

          if (isCustomRouter(route)) {
            return route.route({ heading: <Header /> })
          }

          const { component: Component, path, fullScreenWidth = false } = route

          return (
            <Route
              path={path}
              key={path}
              element={
                <>
                  <Header />
                  <Layout fullScreenWidth={fullScreenWidth}>
                    <Component />
                  </Layout>
                </>
              }
            />
          )
        })}
      </Routes>
    </BrowserRouter>
  )
}

const Layout = styled(FigmaBox)<{ fullScreenWidth?: boolean }>`
  width: 100%;
  ${({ fullScreenWidth }) => (fullScreenWidth ? `` : `max-width: ${BO_LAYOUT_SIZE}px;`)}
  ${({ fullScreenWidth }) => (fullScreenWidth ? `` : `padding: 0 ${Spacings.small};`)}
  ${({ fullScreenWidth }) => (fullScreenWidth ? `` : `margin: 0 auto;`)}
`

export default Router
